import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

function getSessionId() {
    let sid = sessionStorage.getItem('vx_sid');
    if (!sid) {
        sid = Math.random().toString(36).substr(2) + Date.now().toString(36);
        sessionStorage.setItem('vx_sid', sid);
    }
    return sid;
}

function track(data) {
    try {
        const payload = { ...data, session_id: getSessionId(), referrer: document.referrer || '' };
        navigator.sendBeacon?.(`${API}/api/analytics/track`, JSON.stringify(payload))
            || fetch(`${API}/api/analytics/track`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload), keepalive: true });
    } catch {}
}

export function useAnalytics({ listingId, cityId, attractionId, blogSlug } = {}) {
    const location = useLocation();
    const enteredAt = useRef(Date.now());
    const maxScroll = useRef(0);
    const lastPath = useRef('');

    const trackPageExit = useCallback(() => {
        if (lastPath.current) {
            const duration = Math.round((Date.now() - enteredAt.current) / 1000);
            track({ type: 'page_exit', page: lastPath.current, duration, scroll_depth: maxScroll.current });
        }
    }, []);

    useEffect(() => {
        // Track exit of previous page
        trackPageExit();

        // Track new pageview
        const path = location.pathname;
        lastPath.current = path;
        enteredAt.current = Date.now();
        maxScroll.current = 0;

        track({
            type: 'pageview',
            page: path,
            title: document.title,
            listing_id: listingId || '',
            city_id: cityId || '',
            attraction_id: attractionId || '',
            blog_slug: blogSlug || ''
        });

        // Scroll depth tracker
        const handleScroll = () => {
            const scrolled = Math.round((window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100);
            if (scrolled > maxScroll.current) maxScroll.current = scrolled;
        };
        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => window.removeEventListener('scroll', handleScroll);
    }, [location.pathname, listingId, cityId, attractionId, blogSlug, trackPageExit]);

    // Track exit on tab close
    useEffect(() => {
        const handleUnload = () => trackPageExit();
        window.addEventListener('beforeunload', handleUnload);
        return () => window.removeEventListener('beforeunload', handleUnload);
    }, [trackPageExit]);
}

export function trackEvent(action, data = {}) {
    track({ type: 'event', action, page: window.location.pathname, ...data });
}
